import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart3, FlaskConical, RefreshCw, Sparkles, TrendingUp, Activity, Network, GitCompare, BookOpen, Calculator, Wallet,
} from 'lucide-react';

import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  quantListSeries, quantGetSeries, quantKgSummary, quantListStrategySpecs,
  quantGetStrategy, quantStrategyComparison, quantUniverse, quantRegenerateSeries,
  getErrorMessage,
} from '../lib/api';
import EquityChart from '../components/quant/EquityChart';
import RegimeHeatmap from '../components/quant/RegimeHeatmap';
import PromptBuilder from '../components/quant/PromptBuilder';
import StrategyInfoCard from '../components/quant/StrategyInfoCard';
import PromotionGauge from '../components/quant/PromotionGauge';
import StrategyComparison from '../components/quant/StrategyComparison';
import AssetSelect from '../components/quant/AssetSelect';
import AssetCatalog from '../components/quant/AssetCatalog';
import InvestmentSimulator from '../components/quant/InvestmentSimulator';
import AIActionToolbar from '../components/quant/AIActionToolbar';
import LiveQuoteCard from '../components/quant/LiveQuoteCard';
import LiveSignalBadge from '../components/quant/LiveSignalBadge';
import PlaceOrderDialog from '../components/quant/PlaceOrderDialog';
import GlossaryTerm from '../components/quant/GlossaryTerm';
import PortfolioTab from '../components/quant/PortfolioTab';
import WalkForwardTab from '../components/quant/WalkForwardTab';
import WeeklyReviewPreview from '../components/quant/WeeklyReviewPreview';
import { buildContextFromChart } from '../lib/promptCartridge';

const KPI = ({ label, value, hint, term }) => (
  <div className="rounded-lg bg-muted/30 px-3 py-2">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
      {term ? <GlossaryTerm term={term}>{label}</GlossaryTerm> : label}
    </p>
    <p className="text-lg font-bold tabular-nums">{value || '—'}</p>
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;
const fmtNum = (v, d = 2) => (v === null || v === undefined) ? '—' : v.toFixed(d);

const QuantLabPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [seriesList, setSeriesList] = useState([]);
  const [specsByName, setSpecsByName] = useState({});
  const [loadingList, setLoadingList] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentSpec, setCurrentSpec] = useState(null);
  const [currentStrategyMeta, setCurrentStrategyMeta] = useState(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [chartMode, setChartMode] = useState('both');
  const [showVolume, setShowVolume] = useState(true);
  const [kg, setKg] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderTemplate, setBuilderTemplate] = useState('compare-strategies');
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [universe, setUniverse] = useState({ items: [], class_labels: {} });
  // Live signal: propagato da LiveSignalBadge → usato da EquityChart per trigger lines
  const [liveSignal, setLiveSignal] = useState(null);
  // Dialog ordine paper: aperto passando lo snapshot del segnale
  const [orderDialog, setOrderDialog] = useState(null);

  // Carica index series + KG summary + specs + universe
  const refreshAll = useCallback(() => {
    setLoadingList(true);
    Promise.all([
      quantListSeries(), quantKgSummary(), quantListStrategySpecs(), quantUniverse(),
    ])
      .then(([sRes, kRes, specRes, uniRes]) => {
        setSeriesList(sRes.data.items || []);
        setKg(kRes.data);
        const map = {};
        (specRes.data.items || []).forEach(s => { map[s.name] = s; });
        setSpecsByName(map);
        setUniverse(uniRes.data || { items: [], class_labels: {} });
      })
      .catch(e => {
        if (e.response?.status === 503) {
          toast.error('Vault Quant Lab non accessibile. Verifica bind mount Docker.');
        } else {
          toast.error(getErrorMessage(e, 'Errore caricamento'));
        }
      })
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    refreshAll();
  }, [user, navigate, refreshAll]);

  // Strategie e asset disponibili (derivati dall'index)
  const { strategies, assetsByStrategy } = useMemo(() => {
    const strats = new Set();
    const byStrat = {};
    for (const s of seriesList) {
      strats.add(s.strategy);
      if (!byStrat[s.strategy]) byStrat[s.strategy] = [];
      byStrat[s.strategy].push(s);
    }
    return { strategies: Array.from(strats).sort(), assetsByStrategy: byStrat };
  }, [seriesList]);

  // Quando selezioni una strategia, default sul primo asset
  useEffect(() => {
    if (selectedStrategy && assetsByStrategy[selectedStrategy]?.length) {
      const firstAsset = assetsByStrategy[selectedStrategy][0].asset;
      setSelectedAsset(firstAsset);
    }
  }, [selectedStrategy, assetsByStrategy]);

  // Auto-load della serie quando entrambi sono selezionati.
  // GUARD: skip la fetch se la combinazione strategy+asset non è coperta
  // (evita 404 spuri quando si cambia strategia ma asset resta legacy).
  useEffect(() => {
    if (!selectedStrategy || !selectedAsset) return;
    const available = (assetsByStrategy[selectedStrategy] || []).map(x => x.asset);
    if (available.length > 0 && !available.includes(selectedAsset)) {
      // Stato incoerente — pulisco l'asset, l'effetto successivo lo rimette al default
      setCurrentSeries(null);
      return;
    }
    setLoadingSeries(true);
    quantGetSeries(selectedStrategy, selectedAsset)
      .then(r => setCurrentSeries(r.data))
      .catch(e => {
        // Silenzio i 404: succedono solo in race-condition tra cambio strategia/asset
        if (e.response?.status !== 404) {
          toast.error(getErrorMessage(e, 'Errore caricamento serie'));
        }
        setCurrentSeries(null);
      })
      .finally(() => setLoadingSeries(false));
  }, [selectedStrategy, selectedAsset, assetsByStrategy]);

  // Auto-load della spec quando cambia strategia (description, params completi, frontmatter status)
  useEffect(() => {
    if (!selectedStrategy) {
      setCurrentSpec(null);
      setCurrentStrategyMeta(null);
      setComparison(null);
      return;
    }
    quantGetStrategy(selectedStrategy)
      .then(r => {
        setCurrentSpec(r.data.spec || null);
        setCurrentStrategyMeta(r.data);
      })
      .catch(() => {
        // Fallback: usa la spec dalla lista (compatta)
        setCurrentSpec(specsByName[selectedStrategy] || null);
        setCurrentStrategyMeta(null);
      });

    // Carica anche comparison (target + previous_versions + competitors)
    setLoadingComparison(true);
    quantStrategyComparison(selectedStrategy)
      .then(r => setComparison(r.data))
      .catch(() => setComparison(null))
      .finally(() => setLoadingComparison(false));
  }, [selectedStrategy, specsByName]);

  // Default selezione: prima strategia disponibile
  useEffect(() => {
    if (!selectedStrategy && strategies.length) {
      setSelectedStrategy(strategies[0]);
    }
  }, [strategies, selectedStrategy]);

  // Contesto per il prompt builder. Si arricchisce di previous_versions e
  // competitors quando il template è propose-evolution (così Claude vede
  // automaticamente la catena di varianti senza che l'utente li scriva).
  const builderContext = useMemo(() => {
    const base = buildContextFromChart({
      strategy: selectedStrategy,
      asset: selectedAsset,
      tickers: assetsByStrategy[selectedStrategy]?.map(s => s.asset),
      period: currentSeries ? [currentSeries.start, currentSeries.end] : undefined,
      extra: {
        sharpe: currentSeries?.summary?.sharpe,
        n_trades: currentSeries?.summary?.n_trades,
      },
    });
    if (builderTemplate === 'propose-evolution' && comparison) {
      base.diagnosis = currentStrategyMeta?.frontmatter?.status || 'unknown';
      base.previous_versions = (comparison.previous_versions || []).map(p => p.name);
      base.competitors = (comparison.competitors || []).map(c => c.name);
    }
    return base;
  }, [selectedStrategy, selectedAsset, assetsByStrategy, currentSeries, builderTemplate, comparison, currentStrategyMeta]);

  const openBuilderWith = (template) => {
    setBuilderTemplate(template);
    setBuilderOpen(true);
  };

  // Chiamato da AIActionToolbar dopo che AIExecutionDialog completa con successo.
  // Sincronizza l'UI: ricarica strategie/specs/serie e, se è stata creata una nuova
  // strategia eseguibile (lib/strategies/<name>.json), la auto-seleziona. Se è stata
  // creata solo una nota concettuale (knowledge/strategies/<name>.md), informa l'utente
  // che serve ancora runnare il backtest per generare le serie.
  const handleAIComplete = useCallback((result) => {
    const saved = result?.saved_paths || [];
    const score = result?.validation?.score;

    // Cerca un nuovo JSON spec eseguibile
    const specPath = saved.find(p => /^lib\/strategies\/.+\.json$/.test(p));
    const notePath = saved.find(p => /^knowledge\/strategies\/.+\.md$/.test(p));
    const assetPath = saved.find(p => /^knowledge\/assets\/.+\.md$/.test(p));
    const sessionPath = saved.find(p => /^knowledge\/sessions\//.test(p));

    const extractName = (path) => {
      if (!path) return null;
      const m = path.match(/([^/]+)\.(?:json|md)$/);
      return m ? m[1] : null;
    };
    const newStrategyName = extractName(specPath) || extractName(notePath);

    // Ricarica tutto. specsByName e seriesList si aggiornano dal vault on-disk.
    refreshAll();

    if (specPath && newStrategyName) {
      // Strategia eseguibile registrata: triggera auto-backtest+export per generare le serie OHLCV
      const regenToastId = toast.loading(
        `Backtest in corso per "${newStrategyName}"...`,
        { description: 'Sto generando OHLCV+segnali+equity. Può richiedere 10-60s a seconda dell\'universe.', duration: Infinity }
      );
      quantRegenerateSeries(newStrategyName)
        .then((r) => {
          const nFiles = r.data?.files?.length || 0;
          toast.success(
            `✅ Strategia "${newStrategyName}" pronta (score ${score}/100)`,
            {
              id: regenToastId,
              description: `Generati ${nFiles} file di serie. Auto-selezionata nel dropdown.`,
              duration: 8000,
            }
          );
          // Ricarica e auto-seleziona dopo che le serie sono disponibili
          refreshAll();
          setTimeout(() => setSelectedStrategy(newStrategyName), 800);
        })
        .catch((e) => {
          toast.error(
            `Spec salvato ma backtest fallito`,
            {
              id: regenToastId,
              description: getErrorMessage(e, 'Errore regenerate-series') +
                ` — la strategia "${newStrategyName}" esiste come spec ma senza serie. Verifica nel repo Quant Lab.`,
              duration: 15000,
            }
          );
          // Ricarica comunque per far apparire lo spec in specsByName
          refreshAll();
        });
      return;
    } else if (notePath && newStrategyName) {
      toast.info(
        `📝 Nota strategia "${newStrategyName}" salvata (score ${score}/100)`,
        {
          description: `JSON spec non generato dall'AI — la strategia esiste solo come nota concettuale. Riprova chiedendo esplicitamente un blocco \`\`\`json\`\`\` con lo spec completo.`,
          duration: 10000,
        }
      );
    } else if (assetPath) {
      const sym = extractName(assetPath);
      toast.success(`📊 Valutazione di ${sym} salvata (score ${score}/100)`, {
        description: `Path: ${assetPath}`,
        duration: 6000,
      });
    } else if (sessionPath) {
      toast.success(`💾 Sessione AI salvata (score ${score}/100)`, {
        description: `Path: ${sessionPath}`,
        duration: 6000,
      });
    } else if (result?.save_skipped_reason) {
      toast.warning(`Output NON salvato`, {
        description: result.save_skipped_reason,
        duration: 8000,
      });
    } else {
      toast.success(`AI completato (score ${score}/100)`);
    }
  }, [refreshAll]);

  const summary = currentSeries?.summary;

  return (
    <div className="space-y-5 animate-in" data-testid="quant-lab-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <FlaskConical className="w-7 h-7 text-primary" strokeWidth={1.5} />
            Quant Lab — Workbench
          </h1>
          <p className="text-muted-foreground">
            Grafici interattivi · Heatmap regime · Generazione prompt cartridge per Claude Code locale
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/quant')}>
            ← Hub viewer
          </Button>
          <Button variant="ghost" size="icon" onClick={refreshAll} disabled={loadingList}>
            <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Weekly Review Preview — sopra al picker */}
      <WeeklyReviewPreview />

      {/* Picker semplificato: Strategia + Asset primari, CTA AI a destra */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                1 · Strategia
              </label>
              <Select value={selectedStrategy || ''} onValueChange={setSelectedStrategy}>
                <SelectTrigger className="mt-1 h-11 text-base">
                  <SelectValue placeholder="Scegli strategia" />
                </SelectTrigger>
                <SelectContent>
                  {strategies.map(s => {
                    const spec = specsByName[s];
                    return (
                      <SelectItem key={s} value={s}>
                        <span className="font-mono">{s}</span>
                        {spec?.type && (
                          <span className="text-muted-foreground ml-2 text-xs">· {spec.type}</span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-5">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                2 · Asset
              </label>
              <div className="mt-1">
                <AssetSelect
                  value={selectedAsset}
                  onChange={setSelectedAsset}
                  allowedSymbols={(assetsByStrategy[selectedStrategy] || []).map(s => s.asset)}
                  universe={universe.items}
                  classLabels={universe.class_labels}
                  placeholder={selectedStrategy ? 'Scegli asset' : 'Prima scegli strategia'}
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <Button
                className="w-full h-11"
                onClick={() => openBuilderWith('compare-strategies')}
                disabled={!currentSeries}
                data-testid="open-prompt-builder"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Analizza
              </Button>
            </div>
          </div>

          {/* AI Action Toolbar — Migliora / Crea / Simula / Analizza */}
          <div className="mt-3">
            <AIActionToolbar
              strategy={selectedStrategy}
              asset={selectedAsset}
              currentSpec={currentSpec}
              comparison={comparison}
              onSimulateClick={() => {
                // Click sul tab "Simulatore" via dispatching a custom event
                const tabBtn = document.querySelector('[role="tab"][value="simulator"]');
                tabBtn?.click();
              }}
              onOpenAnalyzer={() => openBuilderWith('compare-strategies')}
              onComplete={handleAIComplete}
            />
          </div>

          {/* Riga di contesto: spiega QUANTI e PERCHÉ certi asset */}
          {selectedStrategy && currentSpec && (
            <div className="mt-3 pt-3 border-t flex items-start gap-2 text-xs">
              <span className="shrink-0 mt-0.5">📋</span>
              <div className="text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">
                  {(assetsByStrategy[selectedStrategy] || []).length} asset compatibili
                </span>
                {' '}per <code className="font-mono">{selectedStrategy}</code>:
                {' '}
                <span className="font-mono">
                  {(currentSpec.universe || []).join(' · ')}
                </span>
                {currentSpec.universe_min_inception && (
                  <span className="ml-1">(dal {currentSpec.universe_min_inception})</span>
                )}
                <span className="block mt-1 italic">
                  L'universe è definito nella spec della strategia per garantire
                  liquidità adeguata e coerenza con la logica entry/exit.
                  Per testare la stessa logica su altri asset, crea una variante con
                  <code className="mx-1 font-mono">propose-evolution</code>.
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Strategy info + Promotion gauge + Live quote */}
      {currentSpec && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <StrategyInfoCard spec={currentSpec} />
          <PromotionGauge
            summary={summary}
            status={currentStrategyMeta?.frontmatter?.status}
          />
          {selectedAsset && (
            <LiveQuoteCard
              symbol={selectedAsset}
              assetMeta={universe.items?.find(a => a.symbol === selectedAsset)}
            />
          )}
        </div>
      )}

      {/* LiveSignalBadge — segnale corrente strategia+asset con trigger levels */}
      {selectedStrategy && selectedAsset && currentSpec && (
        <LiveSignalBadge
          strategy={selectedStrategy}
          symbol={selectedAsset}
          assetMeta={universe.items?.find(a => a.symbol === selectedAsset)}
          onSignalUpdate={setLiveSignal}
          onOpenOrder={(snapshot) => setOrderDialog(snapshot)}
        />
      )}

      {/* KPI summary */}
      {summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Metriche · {selectedStrategy} on {selectedAsset}
              {currentSeries && (
                <Badge variant="outline" className="text-[10px] ml-2">
                  {currentSeries.start} → {currentSeries.end}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <KPI term="sharpe" label="Sharpe" value={fmtNum(summary.sharpe)} />
              <KPI term="cagr" label="CAGR" value={fmtPct(summary.cagr)} />
              <KPI term="max_dd" label="Max DD" value={fmtPct(summary.max_dd)} />
              <KPI term="calmar" label="Calmar" value={fmtNum(summary.calmar)} />
              <KPI term="win_rate" label="Win rate" value={fmtPct(summary.win_rate)} />
              <KPI term="n_trades" label="Trades" value={summary.n_trades} hint={`${fmtNum(summary.trades_per_year, 1)}/y`} />
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="chart">
        <TabsList>
          <TabsTrigger value="chart"><TrendingUp className="w-4 h-4 mr-1" /> Grafico</TabsTrigger>
          <TabsTrigger value="simulator"><Calculator className="w-4 h-4 mr-1" /> Simulatore</TabsTrigger>
          <TabsTrigger value="portfolio"><Wallet className="w-4 h-4 mr-1" /> Portfolio</TabsTrigger>
          <TabsTrigger value="walk-forward"><Activity className="w-4 h-4 mr-1" /> Walk-Forward</TabsTrigger>
          <TabsTrigger value="catalog"><BookOpen className="w-4 h-4 mr-1" /> Catalog</TabsTrigger>
          <TabsTrigger value="comparison"><GitCompare className="w-4 h-4 mr-1" /> Confronto</TabsTrigger>
          <TabsTrigger value="heatmap"><Network className="w-4 h-4 mr-1" /> Heatmap regime</TabsTrigger>
          <TabsTrigger value="raw"><BarChart3 className="w-4 h-4 mr-1" /> Raw data</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="mt-4">
          <Card>
            {currentSeries && (
              <CardHeader className="pb-3 border-b">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <TrendingUp className="w-4 h-4 text-primary shrink-0" />
                      <span className="font-mono">{currentSeries.asset}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-sm font-normal">
                        {(universe.items.find(a => a.symbol === currentSeries.asset) || {}).name || ''}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-sm font-mono text-primary">{currentSeries.strategy}</span>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-2 flex-wrap">
                      <span>📅 {currentSeries.start} → {currentSeries.end}</span>
                      <span>·</span>
                      <span>{((new Date(currentSeries.end) - new Date(currentSeries.start)) / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1)} anni</span>
                      <span>·</span>
                      <span>🎯 {currentSeries.markers?.length || 0} segnali</span>
                      <span>·</span>
                      <span>📊 {currentSeries.ohlcv?.length || 0} sedute</span>
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Vista</span>
                    <div className="flex gap-1 rounded-md border bg-muted/20 p-0.5">
                      {[
                        { v: 'both', label: 'Candele + Equity' },
                        { v: 'price', label: 'Solo candele' },
                        { v: 'equity', label: 'Solo equity' },
                      ].map(m => (
                        <button
                          key={m.v}
                          onClick={() => setChartMode(m.v)}
                          className={`text-xs px-2.5 py-1 rounded transition-colors ${
                            chartMode === m.v
                              ? 'bg-primary text-primary-foreground font-medium'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowVolume(v => !v)}
                      className={`text-[10px] px-2 py-0.5 rounded transition-colors border ${
                        showVolume
                          ? 'bg-primary/10 text-primary border-primary/30'
                          : 'text-muted-foreground border-muted hover:text-foreground'
                      }`}
                      title="Mostra/nascondi volumi sotto le candele"
                    >
                      📊 Volumi: {showVolume ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>
              </CardHeader>
            )}
            <CardContent className="pt-4">
              {loadingSeries ? (
                <div className="h-96 bg-muted animate-pulse rounded-lg" />
              ) : (
                <EquityChart
                  data={currentSeries}
                  mode={chartMode}
                  showVolume={showVolume}
                  height={460}
                  liveMode={!!liveSignal}
                  liveSignal={liveSignal}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="portfolio" className="mt-4">
          <PortfolioTab />
        </TabsContent>

        <TabsContent value="walk-forward" className="mt-4">
          <WalkForwardTab
            strategies={strategies}
            defaultStrategy={selectedStrategy}
          />
        </TabsContent>

        <TabsContent value="catalog" className="mt-4">
          <AssetCatalog
            universe={universe.items}
            classLabels={universe.class_labels}
            onApplyStrategy={(symbol, stratName) => {
              // 2 forme: con stratName (da StrategyApplicability) o solo symbol
              // (da bottone "Prova una strategia" — auto-detect prima compatibile)
              const target = stratName || strategies.find(s => {
                const assets = (assetsByStrategy[s] || []).map(x => x.asset);
                return assets.includes(symbol);
              });
              if (target) {
                setSelectedStrategy(target);
                setSelectedAsset(symbol);
                toast.success(`Strategia '${target}' applicata a ${symbol}`);
                return;
              }
              toast.warning(
                `Nessuna strategia esistente copre ${symbol}`,
                {
                  description: 'Apri il tab Confronto su una strategia simile e usa "Proponi evoluzione" per crearne una variante che includa questo asset.',
                  duration: 8000,
                }
              );
            }}
            onProposeEvolution={(stratName, symbol) => {
              // L'utente vuole creare una variante della strategia che includa
              // questo asset. Apro il PromptBuilder pre-caricato.
              setSelectedStrategy(stratName);
              openBuilderWith('propose-evolution');
              toast.info(
                `Builder aperto per evolvere ${stratName}`,
                { description: `Aggiungi nel testo libero: "estendi l'universe per includere ${symbol}"` }
              );
            }}
          />
        </TabsContent>

        <TabsContent value="comparison" className="mt-4">
          {loadingComparison ? (
            <div className="h-64 bg-muted animate-pulse rounded-lg" />
          ) : (
            <StrategyComparison
              comparison={comparison}
              onProposeEvolution={() => openBuilderWith('propose-evolution')}
              onSelectStrategy={(name) => {
                if (specsByName[name]) setSelectedStrategy(name);
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="simulator" className="mt-4">
          {selectedStrategy && selectedAsset ? (
            <InvestmentSimulator
              strategyName={selectedStrategy}
              symbol={selectedAsset}
              assetMeta={universe.items?.find(a => a.symbol === selectedAsset)}
              defaultStart={currentSeries?.start}
              defaultEnd={currentSeries?.end}
            />
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Seleziona strategia e asset per iniziare la simulazione.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="heatmap" className="mt-4">
          <RegimeHeatmap
            byRegime={kg?.top_strategies_per_regime}
            onCellClick={(strategy, regime) => {
              setSelectedStrategy(strategy);
              toast.info(`Selezionata ${strategy}. Apri il builder per analizzare in regime "${regime}".`);
            }}
          />
        </TabsContent>

        <TabsContent value="raw" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              {currentSeries ? (
                <pre className="text-[11px] font-mono max-h-96 overflow-auto bg-muted/40 rounded p-3">
                  {JSON.stringify({
                    strategy: currentSeries.strategy,
                    asset: currentSeries.asset,
                    n_ohlcv: currentSeries.ohlcv?.length,
                    n_markers: currentSeries.markers?.length,
                    n_equity_points: currentSeries.equity_curve?.length,
                    summary: currentSeries.summary,
                    first_5_markers: currentSeries.markers?.slice(0, 5),
                  }, null, 2)}
                </pre>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-8">Nessuna serie selezionata.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog ordine paper — aperto da LiveSignalBadge via onOpenOrder */}
      {orderDialog && (
        <PlaceOrderDialog
          open={!!orderDialog}
          onOpenChange={(v) => { if (!v) setOrderDialog(null); }}
          symbol={orderDialog.symbol}
          strategy={orderDialog.strategy}
          lastPrice={orderDialog.lastPrice}
          accountEquity={100000}
          maxPctPerPosition={currentSpec?.sizing?.max_pct_per_position || 0.2}
          signalSnapshot={orderDialog.signalSnapshot}
          onPlaced={() => {
            toast.success('Posizione paper aperta');
            setOrderDialog(null);
          }}
        />
      )}

      <PromptBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        context={builderContext}
        defaultTemplate={builderTemplate}
        onAIComplete={handleAIComplete}
      />
    </div>
  );
};

export default QuantLabPage;
